import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Flame, Trophy, Target, Crown, Zap, Sprout,
  Check, CheckCircle2, Circle, Clock, TrendingUp, TrendingDown, Minus,
  Users, Lightbulb, ChevronRight, Calendar, BarChart3, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import BackfillDayModal from './BackfillDayModal';
import { formatLocalDate } from '../utils/dateHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const milestoneIcons = {
  sprout: Sprout,
  flame: Flame,
  zap: Zap,
  trophy: Trophy,
  target: Target,
  crown: Crown
};

// ── Calendar Grid (Mon-Sun rows, date numbers inside cells, last N days) ──
function CalendarHeatmap({ heatmapData, onDayClick, maxBackdateDays = 30 }) {
  // Build a calendar showing the last N days as full weeks (Mon-Sun).
  // Cells outside the backfill window are still rendered (for visual continuity)
  // but faded and not tappable.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = formatLocalDate(today);

  // Start of the visible range: maxBackdateDays before today, then snapped back
  // to the Monday of that week so the grid is rectangular.
  const windowStart = new Date(today);
  windowStart.setDate(windowStart.getDate() - maxBackdateDays);

  const gridStart = new Date(windowStart);
  const gridStartDay = gridStart.getDay(); // 0=Sun,1=Mon,...6=Sat
  const daysToMonday = gridStartDay === 0 ? 6 : gridStartDay - 1;
  gridStart.setDate(gridStart.getDate() - daysToMonday);

  // Grid ends at the Sunday of the week containing today.
  const gridEnd = new Date(today);
  const todayWeekday = gridEnd.getDay();
  const daysToSunday = todayWeekday === 0 ? 0 : 7 - todayWeekday;
  gridEnd.setDate(gridEnd.getDate() + daysToSunday);

  const weeks = [];
  const cursor = new Date(gridStart);
  while (cursor <= gridEnd) {
    const row = [];
    for (let i = 0; i < 7; i++) {
      const dateStr = formatLocalDate(cursor);
      const inWindow = cursor >= windowStart && cursor <= today;
      const isFuture = cursor > today;
      const count = heatmapData[dateStr] || 0;
      row.push({
        date: dateStr,
        dayNum: cursor.getDate(),
        count,
        inWindow,
        isFuture,
        isToday: dateStr === todayStr,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(row);
  }

  const cellClass = (cell) => {
    if (cell.isFuture || !cell.inWindow) {
      return 'bg-gray-50 text-gray-300 cursor-default';
    }
    if (cell.count === 0) return 'bg-gray-100 text-gray-500';
    if (cell.count <= 2) return 'bg-emerald-200 text-emerald-900';
    if (cell.count <= 4) return 'bg-emerald-400 text-white';
    return 'bg-emerald-600 text-white';
  };

  return (
    <div className="space-y-1" data-testid="calendar-heatmap">
      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((lbl, i) => (
          <div key={i} className="text-[10px] font-semibold text-muted-foreground text-center uppercase tracking-wide">
            {lbl}
          </div>
        ))}
      </div>

      {weeks.map((row, wi) => (
        <div key={wi} className="grid grid-cols-7 gap-1">
          {row.map((cell, ci) => {
            const tappable = cell.inWindow && !cell.isFuture && onDayClick;
            const showLabel = cell.inWindow && !cell.isFuture;
            return (
              <button
                key={ci}
                type="button"
                disabled={!tappable}
                onClick={() => tappable && onDayClick(cell.date)}
                className={`aspect-square rounded-lg text-xs font-semibold flex items-center justify-center transition-all ${cellClass(cell)} ${
                  tappable
                    ? 'hover:ring-2 hover:ring-primary hover:ring-offset-1 hover:scale-105 active:scale-95'
                    : ''
                } ${cell.isToday ? 'ring-2 ring-indigo-500' : ''}`}
                title={
                  cell.isFuture
                    ? ''
                    : !cell.inWindow
                      ? `${cell.date} — older than ${maxBackdateDays} days`
                      : `${cell.date}: ${cell.count} supplements${tappable ? ' — tap to edit' : ''}`
                }
                data-testid={`heatmap-day-${cell.date}`}
              >
                {showLabel ? cell.dayNum : ''}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── Streak Milestones Bar ──
function MilestoneBar({ milestones, currentStreak }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="milestone-bar">
      {milestones.map((m, i) => {
        const Icon = milestoneIcons[m.icon] || Target;
        const isNext = !m.earned && (i === 0 || milestones[i - 1].earned);
        return (
          <div
            key={m.days}
            className={`flex flex-col items-center min-w-[56px] px-2 py-1.5 rounded-lg transition-all ${
              m.earned ? 'bg-amber-50 border border-amber-200' : isNext ? 'bg-primary/5 border border-primary/20' : 'bg-gray-50 border border-gray-100'
            }`}
          >
            <Icon className={`w-4 h-4 ${m.earned ? 'text-amber-600' : isNext ? 'text-primary' : 'text-gray-300'}`} />
            <span className={`text-[10px] font-bold mt-0.5 ${m.earned ? 'text-amber-700' : isNext ? 'text-primary' : 'text-gray-400'}`}>{m.days}d</span>
            <span className={`text-[9px] leading-tight ${m.earned ? 'text-amber-600' : 'text-gray-400'}`}>{m.name.split(' ')[0]}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Daily Check-In Widget ──
// Supplement list is derived directly from the user's active Reminders (same source as My Plan).
// Excludes medications (Rx) and water reminders so the streak only reflects supplements.
export function DailyCheckInWidget({ getAuthHeader, supplements = [] }) {
  const [todayData, setTodayData] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [heatmapData, setHeatmapData] = useState({});
  const [selectedSupps, setSelectedSupps] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [reminderSupps, setReminderSupps] = useState([]); // deduped names from active reminders
  const [backfillDate, setBackfillDate] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [todayRes, streakRes, heatmapRes, remindersRes] = await Promise.all([
        axios.get(`${API}/checkin/today`, { headers: getAuthHeader() }),
        axios.get(`${API}/checkin/streak`, { headers: getAuthHeader() }),
        axios.get(`${API}/checkin/heatmap`, { headers: getAuthHeader() }),
        axios.get(`${API}/reminders`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setTodayData(todayRes.data);
      setStreakData(streakRes.data);
      setHeatmapData(heatmapRes.data.heatmap || {});
      if (todayRes.data.checked_in) {
        setSelectedSupps(new Set(todayRes.data.supplements_taken));
      }
      // Build deduped supplement list from active reminders. Filter:
      //   - drop disabled reminders
      //   - drop medications (Rx) — they're not supplements
      //   - drop water reminders (cup tracking, not a supplement)
      const reminders = Array.isArray(remindersRes.data) ? remindersRes.data : [];
      const seen = new Set();
      const names = [];
      reminders.forEach(r => {
        if (r.enabled === false) return;
        if (r.is_medication) return;
        const raw = (r.supplement_name || '').trim();
        if (!raw) return;
        if (raw.startsWith('💧') || /water \(cup/i.test(raw)) return;
        const key = raw.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        names.push(raw);
      });
      setReminderSupps(names);
    } catch (err) {
      console.error('Check-in fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSupplement = (name) => {
    setSelectedSupps(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleCheckIn = async (names) => {
    const list = Array.isArray(names) ? names : Array.from(selectedSupps);
    if (list.length === 0) {
      toast.error('Select at least one supplement');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/checkin`, {
        supplements_taken: list
      }, { headers: getAuthHeader() });
      setStreakData({
        current_streak: res.data.current_streak,
        longest_streak: res.data.longest_streak,
        total_checkin_days: res.data.total_checkin_days,
        consistency_30d: res.data.consistency_30d,
        milestones: res.data.milestones
      });
      setSelectedSupps(new Set(list));
      setTodayData({ checked_in: true, supplements_taken: list });
      // Update heatmap
      const today = formatLocalDate(new Date());
      setHeatmapData(prev => ({ ...prev, [today]: list.length }));
      toast.success(`Checked in! ${list.length} supplement${list.length !== 1 ? 's' : ''} logged`);
    } catch (err) {
      toast.error('Failed to save check-in');
    } finally {
      setSaving(false);
    }
  };

  const quickCheckInAll = (list) => {
    if (!list || list.length === 0) return;
    handleCheckIn(list);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-border p-5 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-40 mb-4" />
        <div className="h-20 bg-gray-100 rounded" />
      </div>
    );
  }

  const isCheckedIn = todayData?.checked_in;

  // Source of truth: active Reminders (excluding Rx + water). Fall back to legacy
  // assessment recommendations only if the user has no reminders yet, and finally
  // to a tiny example list so the widget is never empty for first-time users.
  let suppList;
  if (reminderSupps.length > 0) {
    suppList = reminderSupps;
  } else if (supplements.length > 0) {
    suppList = [...new Set(supplements.map(s => s.name))];
  } else {
    suppList = ['Vitamin D3', 'Omega-3', 'Magnesium', 'B-Complex', 'Probiotics'];
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-border overflow-hidden"
      data-testid="daily-checkin-widget"
    >
      {/* Header with streak */}
      <div className="p-5 pb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCheckedIn ? 'bg-emerald-100' : 'bg-amber-100'}`}>
              {isCheckedIn ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              ) : (
                <Flame className="w-5 h-5 text-amber-600" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-sm text-[#1A1A1A]">
                {isCheckedIn ? 'Checked In Today' : 'Daily Check-In'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {isCheckedIn 
                  ? `${todayData.supplements_taken.length} supplements logged`
                  : 'Log your supplements to build your streak'
                }
              </p>
            </div>
          </div>
          {streakData && streakData.current_streak > 0 && (
            <div className="flex items-center gap-1 bg-amber-50 px-3 py-1.5 rounded-full border border-amber-200">
              <Flame className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-bold text-amber-700">{streakData.current_streak}</span>
              <span className="text-xs text-amber-600">day{streakData.current_streak !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>

        {/* Stats + milestones moved to Dashboard hero card + /progress page to
            avoid duplication. Keep only actionable check-in UI here. */}

        {/* One-tap shortcut: log everything in stack for today */}
        {!isCheckedIn && suppList.length > 0 && (
          <Button
            onClick={() => quickCheckInAll(suppList)}
            disabled={saving}
            className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="checkin-took-everything-btn"
          >
            {saving ? (
              <span className="flex items-center gap-2"><Check className="w-4 h-4 animate-pulse" /> Logging…</span>
            ) : (
              <span className="flex items-center gap-2"><Check className="w-4 h-4" /> I took everything today ({suppList.length})</span>
            )}
          </Button>
        )}
      </div>

      {/* Supplement Check-In List */}
      <div className="border-t border-border">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <span className="text-sm font-medium text-[#1A1A1A]">
            {isCheckedIn ? 'Update Check-In' : 'Check In Now'}
          </span>
          <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
        
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-5 pb-4">
                <div className="grid grid-cols-2 gap-2 mb-3 max-h-48 overflow-y-auto">
                  {suppList.map((name) => (
                    <button
                      key={name}
                      onClick={() => toggleSupplement(name)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all text-xs ${
                        selectedSupps.has(name)
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid={`checkin-supp-${name.replace(/\s/g, '-').toLowerCase()}`}
                    >
                      {selectedSupps.has(name) ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                      ) : (
                        <Circle className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                      )}
                      <span className="truncate">{name}</span>
                    </button>
                  ))}
                </div>
                <Button
                  onClick={handleCheckIn}
                  disabled={saving || selectedSupps.size === 0}
                  className="w-full btn-primary"
                  data-testid="checkin-submit-btn"
                >
                  {saving ? 'Saving...' : isCheckedIn ? 'Update Check-In' : `Check In (${selectedSupps.size} selected)`}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Heatmap */}
      {Object.keys(heatmapData).length > 0 && (
        <div className="border-t border-border p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-[#1A1A1A] flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
              Activity
              <span className="ml-1 text-[10px] font-normal text-muted-foreground italic">
                Tap any day to backfill (last 30 days)
              </span>
            </h4>
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span>Less</span>
              <div className="w-2.5 h-2.5 rounded-sm bg-gray-100" />
              <div className="w-2.5 h-2.5 rounded-sm bg-emerald-200" />
              <div className="w-2.5 h-2.5 rounded-sm bg-emerald-400" />
              <div className="w-2.5 h-2.5 rounded-sm bg-emerald-600" />
              <span>More</span>
            </div>
          </div>
          <CalendarHeatmap
            heatmapData={heatmapData}
            onDayClick={(d) => setBackfillDate(d)}
          />
        </div>
      )}

      <BackfillDayModal
        open={!!backfillDate}
        date={backfillDate}
        onClose={() => setBackfillDate(null)}
        onSaved={() => {
          // Refresh heatmap so newly-logged days light up immediately.
          fetchData();
        }}
        getAuthHeader={getAuthHeader}
      />
    </motion.div>
  );
}

// ── Health Score Comparison Widget ──
export function ScoreComparisonWidget({ getAuthHeader }) {
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/checkin/comparison`, { headers: getAuthHeader() });
        setComparison(res.data);
      } catch (err) {
        console.error('Comparison fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [getAuthHeader]);

  if (loading || !comparison?.has_comparison) return null;

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  const categories = Object.entries(comparison.changes)
    .filter(([, v]) => v.change !== 0)
    .sort(([, a], [, b]) => b.change - a.change);

  if (categories.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-border p-5"
      data-testid="score-comparison-widget"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="font-semibold text-sm text-[#1A1A1A]">Your Progress</h3>
          <p className="text-xs text-muted-foreground">
            {formatDate(comparison.first_assessment_date)} vs {formatDate(comparison.latest_assessment_date)}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {categories.slice(0, 6).map(([cat, data]) => (
          <div key={cat} className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground w-24 truncate capitalize">
              {cat.replace('_', ' ')}
            </span>
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${data.change > 0 ? 'bg-emerald-500' : 'bg-red-400'}`}
                style={{ width: `${data.latest}%` }}
              />
            </div>
            <div className={`flex items-center gap-0.5 text-xs font-medium min-w-[50px] justify-end ${
              data.change > 0 ? 'text-emerald-600' : 'text-red-500'
            }`}>
              {data.change > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {data.change > 0 ? '+' : ''}{data.change}%
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
        <Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <p className="text-xs text-blue-800">
          {comparison.improved_count > comparison.declined_count
            ? `${comparison.improved_count} areas improved since your first assessment!`
            : `Retake the quiz after 30 days of supplementation to track changes.`}
        </p>
      </div>
    </motion.div>
  );
}

// ── Community Stats Widget ──
export function CommunityStatsWidget({ getAuthHeader }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/checkin/community-stats`, { headers: getAuthHeader() });
        setStats(res.data);
      } catch (err) {
        console.error('Community stats error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [getAuthHeader]);

  if (loading || !stats) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-border p-5"
      data-testid="community-stats-widget"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
          <Users className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="font-semibold text-sm text-[#1A1A1A]">Vitala Community</h3>
          <p className="text-xs text-muted-foreground">See how you compare</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="p-3 rounded-xl bg-gray-50 text-center">
          <p className="text-lg font-bold text-[#1A1A1A]">{stats.your_consistency_30d}%</p>
          <p className="text-[10px] text-muted-foreground">Your Consistency</p>
        </div>
        <div className="p-3 rounded-xl bg-gray-50 text-center">
          <p className="text-lg font-bold text-[#1A1A1A]">{stats.community_avg_consistency_30d}%</p>
          <p className="text-[10px] text-muted-foreground">Community Avg</p>
        </div>
      </div>

      {stats.your_consistency_30d > stats.community_avg_consistency_30d && (
        <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100 mb-3">
          <p className="text-xs text-emerald-800 font-medium">
            You're more consistent than the community average! Keep it up.
          </p>
        </div>
      )}

      {stats.popular_supplements.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">Trending This Week</h4>
          <div className="flex flex-wrap gap-1.5">
            {stats.popular_supplements.map((s, i) => (
              <span key={i} className="px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 text-[10px] font-medium border border-purple-100">
                {s.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ── Daily Tip Widget ──
export function DailyTipWidget({ getAuthHeader }) {
  const [tip, setTip] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/checkin/daily-tip`, { headers: getAuthHeader() });
        setTip(res.data.tip);
      } catch (err) {
        console.error('Daily tip error:', err);
      }
    };
    fetch();
  }, [getAuthHeader]);

  if (!tip) return null;

  const iconMap = {
    clock: Clock, moon: Clock, zap: Zap, target: Target, activity: BarChart3,
    layers: BarChart3, heart: TrendingUp, sun: Sparkles, droplets: Sparkles,
    utensils: Sparkles, dumbbell: Sparkles, 'bar-chart': BarChart3, coffee: Clock,
    shield: Target, brain: Lightbulb, thermometer: Clock, sunrise: Sparkles,
    'alert-triangle': Target, flask: Sparkles, trophy: Trophy
  };
  const TipIcon = iconMap[tip.icon] || Lightbulb;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-5"
      data-testid="daily-tip-widget"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
          <TipIcon className="w-5 h-5 text-amber-700" />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-sm text-amber-900">Tip of the Day</h3>
            <Lightbulb className="w-3.5 h-3.5 text-amber-600" />
          </div>
          <p className="text-xs font-medium text-amber-800 mb-1">{tip.title}</p>
          <p className="text-xs text-amber-700 leading-relaxed">{tip.tip}</p>
        </div>
      </div>
    </motion.div>
  );
}


// ── Wellness Journal Widget ──
export function WellnessJournalWidget({ getAuthHeader }) {
  const [todayLog, setTodayLog] = useState(null);
  const [scores, setScores] = useState({ energy: 3, sleep_quality: 3, mood: 3, digestion: 3, focus: 3 });
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [backfillDate, setBackfillDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const todayStr = formatLocalDate(new Date());
  const minBackfillDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return formatLocalDate(d);
  })();

  const metrics = [
    { key: 'energy', label: 'Energy', emoji: '⚡' },
    { key: 'sleep_quality', label: 'Sleep', emoji: '🌙' },
    { key: 'mood', label: 'Mood', emoji: '😊' },
    { key: 'digestion', label: 'Digestion', emoji: '🍃' },
    { key: 'focus', label: 'Focus', emoji: '🎯' },
  ];

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/wellness/today`, { headers: getAuthHeader() });
        setTodayLog(res.data);
        if (res.data.logged && res.data.scores) {
          setScores(res.data.scores);
          setNotes(res.data.notes || '');
        }
      } catch (err) {
        console.error('Wellness fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [getAuthHeader]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/wellness/log`, { ...scores, notes }, { headers: getAuthHeader() });
      setTodayLog({ logged: true, scores, notes });
      toast.success('Wellness log saved!');
      setExpanded(false);
    } catch (err) {
      toast.error('Failed to save wellness log');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-border overflow-hidden"
      data-testid="wellness-journal-widget"
    >
      <div className="p-5 pb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${todayLog?.logged ? 'bg-emerald-100' : 'bg-violet-100'}`}>
              <BarChart3 className={`w-5 h-5 ${todayLog?.logged ? 'text-emerald-600' : 'text-violet-600'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-[#1A1A1A]">
                {todayLog?.logged ? 'Wellness Logged' : 'How Are You Feeling?'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {todayLog?.logged ? 'Tap to update' : 'Quick 30-second daily log'}
              </p>
            </div>
          </div>
        </div>

        {/* Quick score preview when logged */}
        {todayLog?.logged && !expanded && (
          <div className="flex gap-2 mt-2">
            {metrics.map(m => (
              <div key={m.key} className="flex-1 text-center p-1.5 rounded-lg bg-gray-50">
                <p className="text-xs">{m.emoji}</p>
                <p className="text-xs font-bold text-[#1A1A1A]">{todayLog.scores[m.key]}/5</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-border">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <span className="text-sm font-medium text-[#1A1A1A]">
            {todayLog?.logged ? 'Update Log' : 'Log Now'}
          </span>
          <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-5 pb-4 space-y-3">
                {metrics.map(m => (
                  <div key={m.key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-[#1A1A1A]">{m.emoji} {m.label}</span>
                      <span className="text-xs font-bold text-primary">{scores[m.key]}/5</span>
                    </div>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(val => (
                        <button
                          key={val}
                          onClick={() => setScores(prev => ({ ...prev, [m.key]: val }))}
                          className={`flex-1 h-8 rounded-lg text-xs font-medium transition-all ${
                            scores[m.key] >= val
                              ? 'bg-primary text-white'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                          data-testid={`wellness-${m.key}-${val}`}
                        >
                          {val}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                <div>
                  <label className="text-xs font-medium text-[#1A1A1A] block mb-1">Notes (optional)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g., Skipped magnesium today, noticed leg cramps..."
                    className="w-full text-xs p-2.5 rounded-lg border border-border resize-none h-16 focus:ring-1 focus:ring-primary focus:border-primary"
                    data-testid="wellness-notes"
                  />
                </div>

                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full btn-primary"
                  data-testid="wellness-save-btn"
                >
                  {saving ? 'Saving...' : todayLog?.logged ? 'Update Log' : 'Save Wellness Log'}
                </Button>

                {/* Backfill a past day */}
                <div className="pt-2 border-t border-border">
                  {!showDatePicker ? (
                    <button
                      type="button"
                      onClick={() => setShowDatePicker(true)}
                      className="w-full flex items-center justify-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium py-1"
                      data-testid="wellness-backfill-toggle"
                    >
                      <Calendar className="w-3.5 h-3.5" />
                      Log for a past day
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        max={todayStr}
                        min={minBackfillDate}
                        defaultValue={todayStr}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v && v !== todayStr) {
                            setBackfillDate(v);
                            setShowDatePicker(false);
                          }
                        }}
                        className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-border"
                        data-testid="wellness-backfill-date-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowDatePicker(false)}
                        className="text-xs text-muted-foreground hover:text-foreground"
                        data-testid="wellness-backfill-cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <BackfillDayModal
        open={!!backfillDate}
        date={backfillDate}
        onClose={() => setBackfillDate(null)}
        getAuthHeader={getAuthHeader}
      />
    </motion.div>
  );
}

// ── Re-Assessment Prompt Widget ──
export function ReAssessmentPromptWidget({ getAuthHeader, latestAssessmentDate, assessmentCount }) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !latestAssessmentDate) return null;

  const daysSince = Math.floor(
    (new Date() - new Date(latestAssessmentDate)) / (1000 * 60 * 60 * 24)
  );

  if (daysSince < 90) return null;

  // Seasonal messaging
  const month = new Date().getMonth();
  let seasonMessage = '';
  if (month >= 11 || month <= 1) seasonMessage = "Winter is here — your vitamin D and immunity needs may have changed.";
  else if (month >= 2 && month <= 4) seasonMessage = "Spring is here — allergy season may shift your supplement needs.";
  else if (month >= 5 && month <= 7) seasonMessage = "Summer heat affects hydration and electrolyte needs.";
  else seasonMessage = "Fall is approaching — time to prepare your immune system for cold season.";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-5"
      data-testid="reassessment-prompt"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-sm text-blue-900 mb-1">Time to Update Your Answers</h3>
            <p className="text-xs text-blue-700 mb-1">
              It's been {daysSince} days since your last assessment. {seasonMessage}
            </p>
            <p className="text-xs text-blue-600">
              Your answers will be pre-filled — just update what's changed.
            </p>
          </div>
        </div>
        <button onClick={() => setDismissed(true)} className="text-blue-400 hover:text-blue-600">
          <span className="sr-only">Dismiss</span>
          ×
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        <a href="/quiz?prefill=true" className="flex-1 text-center py-2 px-4 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors">
          Update My Answers
        </a>
      </div>
    </motion.div>
  );
}

// ── My Stack Dashboard Card ──
export function MyStackCard({ getAuthHeader }) {
  const [stack, setStack] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/stack`, { headers: getAuthHeader() });
        setStack(res.data);
      } catch (err) {
        console.error('Stack fetch error:', err);
      }
    };
    fetch();
  }, [getAuthHeader]);

  if (!stack || stack.total_supplements === 0) return null;

  const { schedule = {}, restock_alerts = [] } = stack;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-border p-5"
      data-testid="mystack-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Target className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-sm text-[#1A1A1A]">My Stack</h3>
            <p className="text-xs text-muted-foreground">{stack.total_supplements} supplements</p>
          </div>
        </div>
        <a href="/my-stack" className="text-xs text-primary font-medium hover:underline flex items-center gap-1" data-testid="mystack-view-all">
          View All <ChevronRight className="w-3 h-3" />
        </a>
      </div>

      {/* Quick schedule */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[
          { key: 'morning', label: 'AM', color: 'bg-amber-50 text-amber-700' },
          { key: 'afternoon', label: 'PM', color: 'bg-sky-50 text-sky-700' },
          { key: 'evening', label: 'EVE', color: 'bg-indigo-50 text-indigo-700' },
        ].map(t => (
          <div key={t.key} className={`rounded-lg p-2 text-center ${t.color}`}>
            <p className="text-[10px] font-semibold mb-0.5">{t.label}</p>
            <p className="text-xs font-bold">{(schedule[t.key] || []).length}</p>
          </div>
        ))}
      </div>

      {/* Restock alerts */}
      {restock_alerts.length > 0 && (
        <div className="p-2 rounded-lg bg-amber-50 border border-amber-100">
          <p className="text-[10px] font-medium text-amber-800">
            ⚠ {restock_alerts[0].name} — {restock_alerts[0].days_until_restock <= 0 ? 'Restock now!' : `${restock_alerts[0].days_until_restock} days left`}
          </p>
        </div>
      )}
    </motion.div>
  );
}
